import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Building, Target, Award, Users } from 'lucide-react';

const About = () => {
  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 gradient-hero" />
        <div className="absolute inset-0 pattern-bg" />
        
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-16 lg:py-24">
          <div className="flex items-center gap-3 mb-6">
            <div className="icon-container w-12 h-12 rounded-xl flex items-center justify-center">
              <Building className="h-6 w-6 text-foreground" />
            </div>
            <span className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Our Story</span>
          </div>
          
          <h1 className="font-display text-4xl lg:text-5xl xl:text-6xl text-foreground mb-6">
            About Us
          </h1>
          <p className="text-lg lg:text-xl text-muted-foreground max-w-2xl">
            First Choice Business Brokers is a national network of business brokerage 
            professionals dedicated to helping owners achieve successful transitions.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="bg-background relative">
        <div className="absolute inset-0 pattern-bg opacity-20" />
        
        <div className="relative max-w-5xl mx-auto px-6 lg:px-8 py-14 lg:py-20">
          <div className="space-y-16">
            {/* Mission */}
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="icon-container w-10 h-10 rounded-lg flex items-center justify-center">
                    <Target className="h-5 w-5 text-foreground" />
                  </div>
                  <h2 className="font-display text-2xl text-foreground">Our Mission</h2>
                </div>
                <p className="text-muted-foreground leading-relaxed">
                  For over two decades, First Choice Business Brokers has helped thousands of business 
                  owners navigate the complex process of selling their companies. Our mission is to 
                  provide professional, confidential advisory services that maximize value and minimize 
                  stress during one of the most significant financial decisions of your life.
                </p>
              </div>
              <div className="feature-card p-8 rounded-2xl">
                <div className="text-4xl font-bold text-gradient mb-2">25+</div>
                <div className="text-muted-foreground">Years of dedicated service to business owners</div>
              </div>
            </div>

            {/* Approach */}
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <div className="feature-card p-8 rounded-2xl order-2 lg:order-1">
                <div className="text-4xl font-bold text-gradient mb-2">Education First</div>
                <div className="text-muted-foreground">We believe knowledge empowers better decisions</div>
              </div>
              <div className="order-1 lg:order-2">
                <div className="flex items-center gap-3 mb-4">
                  <div className="icon-container w-10 h-10 rounded-lg flex items-center justify-center">
                    <Users className="h-5 w-5 text-foreground" />
                  </div>
                  <h2 className="font-display text-2xl text-foreground">Our Approach</h2>
                </div>
                <p className="text-muted-foreground leading-relaxed">
                  We believe in education-first engagement. Before asking for your business, we want to 
                  ensure you have the knowledge to make informed decisions. Our Newsletter Library is a 
                  reflection of this philosophy—providing practical, actionable guidance to business 
                  owners at every stage of their journey.
                </p>
              </div>
            </div>

            {/* Experience */}
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="icon-container w-10 h-10 rounded-lg flex items-center justify-center">
                    <Award className="h-5 w-5 text-foreground" />
                  </div>
                  <h2 className="font-display text-2xl text-foreground">Experience That Matters</h2>
                </div>
                <p className="text-muted-foreground leading-relaxed">
                  Our team has facilitated thousands of successful transactions across diverse industries. 
                  This experience informs every piece of content we create and every conversation we have 
                  with business owners. We've seen what works, what doesn't, and what separates good 
                  outcomes from great ones.
                </p>
              </div>
              <div className="feature-card p-8 rounded-2xl">
                <div className="text-4xl font-bold text-gradient mb-2">5,000+</div>
                <div className="text-muted-foreground">Successful transactions completed</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[hsl(350,50%,25%)]" />
        <div className="absolute inset-0 pattern-bg opacity-10" />
        
        <div className="relative max-w-5xl mx-auto px-6 lg:px-8 py-16 lg:py-20 text-center">
          <h3 className="font-display text-2xl lg:text-3xl text-white mb-4">
            Explore Our Resources
          </h3>
          <p className="text-white/80 mb-8 max-w-lg mx-auto">
            Access our library of newsletters covering valuation, deal structure, 
            tax planning, and more.
          </p>
          <Link
            to="/newsletters"
            className="bg-white text-[hsl(350,50%,25%)] px-8 py-4 rounded-xl font-semibold inline-flex items-center gap-2 hover:bg-white/90 transition-smooth"
          >
            Browse Newsletter Library
            <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
